import { Component, Inject, OnInit } from '@angular/core';
import {
    AppNotificationService,
    AppNotificationType,
    BaseCancellationComponent,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    EscalationInstanceToken,
    EscalationInterface,
    EventService,
    isDefined,
    isNgRxEnabled,
    MaterialService,
    NOTIFICATION_STATUS_CODE,
    notificationCancelled,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    PaceState,
    RecipientsInstanceToken,
    RecipientsInterface,
    StatusModel,
    SynchronizationService,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { cleanupFormList, FormsService } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { AlertService } from '../../../core/services/eta-alert/alert.service';
import { LocalStorageSalesQuoteService } from '../../../material/services/local-storage-sales-quote.service';
import { OdataCancellationService } from './odata-cancellation.service';

@Component({
    selector: 'pace-core-cancellation',
    templateUrl: './cancellation.component.html',
    styleUrls: ['./cancellation.component.scss'],
    standalone: false,
})
export class CancellationComponent extends BaseCancellationComponent implements OnInit {
    public modalResult = new Subject<ICancellationResult>();
    public isNotificationInProgressAndCancelled = false;
    public cancellationLabel: string;
    public isLoading = false;

    private readonly ngrxEnabled = isNgRxEnabled();

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly odataCancellationService: OdataCancellationService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly formsService: FormsService,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly appNotificationService: AppNotificationService,
        private readonly synchronizationService: SynchronizationService,
        @Inject(EscalationInstanceToken) private readonly escalationService: EscalationInterface,
        private readonly eventService: EventService,
        private readonly localStorageSalesQuoteService: LocalStorageSalesQuoteService,
        private readonly materialService: MaterialService,
        private readonly store: Store<PaceState>,
        public readonly modalInstance: BsModalRef,
        private readonly alertService: AlertService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(RecipientsInstanceToken) private readonly recipientsService: RecipientsInterface,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.notificationService
            .isNotificationInProgressAndCancelledAsync(this.model.notification)
            .then((isNotificationInProgressAndCancelled) => {
                this.isNotificationInProgressAndCancelled = isNotificationInProgressAndCancelled;
                this.cancellationLabel = this.isNotificationInProgressAndCancelled
                    ? <string>this.translate.instant('DASHBOARD_NOTIFICATION_CANCELLATION_IN_PROGRESS_BUTTON_LABEL')
                    : <string>this.translate.instant('DASHBOARD_NOTIFICATION_CANCELATION_CONFIRM_CANCELATION_LABEL');
            })
            .catch((error) => {
                this.log.error(
                    this.constructor.name,
                    ' ngOnInit: error determining if notification is in progress and cancelled',
                    error,
                );
            });
    }

    public sendCancellation(): void {
        this.isLoading = true;
        this.odataCancellationService
            .cancelNotification(this.model)
            .pipe(
                take(1),
                mergeMap(() => this.updateNotificationStatus()),
                tap(() => {
                    this.announceCancellation();
                    this.isLoading = false;
                    this.modalResult.next({
                        notificationUpdateKey: this.model.notificationUpdateKey,
                    });
                    this.modalResult.complete();
                    this.modalInstance.hide();
                }),
                mergeMap(() => this.cleanupNotificationData()),
                mergeMap(() => {
                    this.log.debug('CancellationComponent: sendCancellation: sendOfflineData');
                    return this.synchronizationService.sendOfflineData();
                }),
                catchError((error) => {
                    this.log.error(`CancellationComponent: sendCancellation: error:`, error);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('DASHBOARD_NOTIFICATION_CANCELLATION_REQUEST_ERROR_TITLE'),
                        <string>this.translate.instant('DASHBOARD_NOTIFICATION_CANCELLATION_REQUEST_ERROR_BODY'),
                    );
                    this.isLoading = false;
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    /**
     * Announces the cancellation so that the dashboard and the detail screens drop the notification
     * they are still showing: a store dispatch once the notification domain is served by the slice,
     * and the `EventService` event the service behind `NotificationDataInstanceToken` listens to
     * while it is not. Either way it reaches the readers through `notificationsUpdated$()`.
     */
    private announceCancellation(): void {
        if (this.ngrxEnabled) {
            this.store.dispatch(notificationCancelled());
            return;
        }
        this.eventService.triggerNotificationsUpdated(true);
    }

    public updateNotificationStatus(): Observable<unknown> {
        if (this.isNotificationInProgressAndCancelled) {
            return this.notificationService.saveCancellationConfirmationForInprogress(
                this.model.notification.identifier,
            );
        } else {
            const statusCodesToSearch = [
                NOTIFICATION_STATUS_CODE.DISP,
                NOTIFICATION_STATUS_CODE.ETA,
                NOTIFICATION_STATUS_CODE.DURA,
                NOTIFICATION_STATUS_CODE.CANC,
            ];
            // set the status to cancelled
            let status: StatusModel | undefined;
            for (const statusCode of statusCodesToSearch) {
                status = this.notificationService.getNotificationStatusByStatusType(
                    this.model.notification,
                    statusCode,
                );
                if (status) {
                    break;
                }
            }

            if (status) {
                status.statusCode = NOTIFICATION_STATUS_CODE.CNCL;
            } else {
                const now = moment().startOf('minute');
                status = new StatusModel(NOTIFICATION_STATUS_CODE.CNCL, now.clone(), now.clone());
            }
            return this.notificationService.saveStatus(this.model.notification, status);
        }
    }

    /**
     * Once cancellation is confirmed by the user the cancelled notification won't get displayed anymore
     * in the dashboard, and also should not be part of NDP next sync onwards.
     * Hence relevant data should be deleted from the local storage.
     *
     * @returns {Observable<void>} Observable that does the cleanup
     * @throws {Error} if the notification does not have a main equipment
     */
    public cleanupNotificationData(): Observable<unknown> {
        this.escalationService.removeEscalationTimer(this.model.notification);
        if (this.configService.getDisplayEtaAlert()) {
            this.alertService.removeEtaTimer(this.model.notification);
        }

        const init$ = this.materialService.deleteRequestedMaterialFromLocalStorage(this.model.notification.identifier);

        if (this.clientManager.isNgmP58()) {
            this.localStorageSalesQuoteService
                .removeSalesQuoteFromLocalStorageAsync(this.model.notification.identifier)
                .catch((e) =>
                    this.log.error(this.constructor.name, 'failed to remove sales quote from local storage', e),
                );

            const mainEquipment = this.model.notification.getMainEquipment();
            if (!mainEquipment) {
                throw new Error(`Notification ${this.model.notification.identifier.toString()} has no main equipment!`);
            }

            return init$.pipe(
                mergeMap(() =>
                    this.recipientsService.removeRecipientsFromLocalStorage(this.model.notification.identifier),
                ),
                mergeMap(() =>
                    this.formsService
                        .getFormList(
                            mainEquipment.matnr.value,
                            mainEquipment.equnr.value,
                            {
                                frontId: this.model.notification.identifier.frontId,
                                qmNum: this.model.notification.identifier.qmNum,
                                maNum: this.model.notification.identifier.maNum,
                            },
                            true,
                        )
                        .pipe(
                            take(1),
                            filter((formList) => isDefined(formList)),
                            map((formList) => {
                                this.store.dispatch(cleanupFormList({ formList }));
                                return undefined;
                            }),
                            catchError(() => {
                                this.log.debug(
                                    `CancellationComponent: cleanupNotificationData: Form list cleanup not necessary since there is no list`,
                                );
                                return of(undefined);
                            }),
                        ),
                ),
            );
        }

        return init$;
    }
}

export interface ICancellationResult {
    notificationUpdateKey: string;
}
